/**
 * Unified Timezone Utilities
 * 
 * Single source of truth for timezone handling across the application.
 * Uses Microsoft Bookings format: "(UTC±HH:MM) Display Name"
 * 
 * All times are stored in UTC in the database and converted to/from
 * the user's selected timezone for display.
 */

export interface TimezoneInfo {
    value: string;        // IANA timezone ID (e.g., 'America/New_York')
    label: string;        // Display label: '(UTC-05:00) Eastern Time (US & Canada)'
    offsetHours: number;  // Standard offset in hours (not accounting for DST)
}

/**
 * Comprehensive timezone list with Microsoft Bookings format labels.
 * Sorted by UTC offset from west to east.
 */
export const TIMEZONES: TimezoneInfo[] = [
    // UTC-12 to UTC-10
    { value: 'Etc/GMT+12', label: '(UTC-12:00) International Date Line West', offsetHours: -12 },
    { value: 'Pacific/Midway', label: '(UTC-11:00) Coordinated Universal Time-11', offsetHours: -11 },
    { value: 'Pacific/Honolulu', label: '(UTC-10:00) Hawaii', offsetHours: -10 },
    
    // UTC-9 to UTC-7
    { value: 'America/Anchorage', label: '(UTC-09:00) Alaska', offsetHours: -9 },
    { value: 'America/Los_Angeles', label: '(UTC-08:00) Pacific Time (US & Canada)', offsetHours: -8 },
    { value: 'America/Phoenix', label: '(UTC-07:00) Arizona', offsetHours: -7 },
    { value: 'America/Denver', label: '(UTC-07:00) Mountain Time (US & Canada)', offsetHours: -7 },
    
    // UTC-6 to UTC-4
    { value: 'America/Chicago', label: '(UTC-06:00) Central Time (US & Canada)', offsetHours: -6 },
    { value: 'America/Mexico_City', label: '(UTC-06:00) Mexico City', offsetHours: -6 },
    { value: 'America/New_York', label: '(UTC-05:00) Eastern Time (US & Canada)', offsetHours: -5 },
    { value: 'America/Indiana/Indianapolis', label: '(UTC-05:00) Indiana (East)', offsetHours: -5 },
    { value: 'America/Bogota', label: '(UTC-05:00) Bogota, Lima, Quito', offsetHours: -5 },
    { value: 'America/Halifax', label: '(UTC-04:00) Atlantic Time (Canada)', offsetHours: -4 },
    { value: 'America/Caracas', label: '(UTC-04:00) Caracas', offsetHours: -4 },
    
    // UTC-3 to UTC-1
    { value: 'America/Sao_Paulo', label: '(UTC-03:00) Brasilia', offsetHours: -3 },
    { value: 'America/Argentina/Buenos_Aires', label: '(UTC-03:00) Buenos Aires', offsetHours: -3 },
    { value: 'Atlantic/South_Georgia', label: '(UTC-02:00) Mid-Atlantic', offsetHours: -2 },
    { value: 'Atlantic/Azores', label: '(UTC-01:00) Azores', offsetHours: -1 },
    { value: 'Atlantic/Cape_Verde', label: '(UTC-01:00) Cape Verde Islands', offsetHours: -1 },
    
    // UTC+0
    { value: 'UTC', label: '(UTC+00:00) Coordinated Universal Time', offsetHours: 0 },
    { value: 'Europe/London', label: '(UTC+00:00) London, Edinburgh, Dublin', offsetHours: 0 },
    { value: 'Africa/Casablanca', label: '(UTC+00:00) Casablanca', offsetHours: 0 },
    
    // UTC+1 to UTC+3
    { value: 'Europe/Paris', label: '(UTC+01:00) Brussels, Paris, Madrid', offsetHours: 1 },
    { value: 'Europe/Berlin', label: '(UTC+01:00) Amsterdam, Berlin, Rome', offsetHours: 1 },
    { value: 'Africa/Lagos', label: '(UTC+01:00) West Central Africa', offsetHours: 1 },
    { value: 'Europe/Athens', label: '(UTC+02:00) Athens, Bucharest', offsetHours: 2 },
    { value: 'Africa/Cairo', label: '(UTC+02:00) Cairo', offsetHours: 2 },
    { value: 'Africa/Johannesburg', label: '(UTC+02:00) Harare, Pretoria', offsetHours: 2 },
    { value: 'Asia/Jerusalem', label: '(UTC+02:00) Jerusalem', offsetHours: 2 },
    { value: 'Europe/Moscow', label: '(UTC+03:00) Moscow, St. Petersburg', offsetHours: 3 },
    { value: 'Asia/Kuwait', label: '(UTC+03:00) Kuwait, Riyadh', offsetHours: 3 },
    { value: 'Africa/Nairobi', label: '(UTC+03:00) Nairobi', offsetHours: 3 },
    
    // UTC+3:30 to UTC+5:30
    { value: 'Asia/Tehran', label: '(UTC+03:30) Tehran', offsetHours: 3.5 },
    { value: 'Asia/Dubai', label: '(UTC+04:00) Abu Dhabi, Muscat', offsetHours: 4 },
    { value: 'Asia/Baku', label: '(UTC+04:00) Baku', offsetHours: 4 },
    { value: 'Asia/Kabul', label: '(UTC+04:30) Kabul', offsetHours: 4.5 },
    { value: 'Asia/Karachi', label: '(UTC+05:00) Islamabad, Karachi', offsetHours: 5 },
    { value: 'Asia/Tashkent', label: '(UTC+05:00) Tashkent', offsetHours: 5 },
    { value: 'Asia/Kolkata', label: '(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi', offsetHours: 5.5 },
    
    // UTC+5:45 to UTC+7
    { value: 'Asia/Kathmandu', label: '(UTC+05:45) Kathmandu', offsetHours: 5.75 },
    { value: 'Asia/Dhaka', label: '(UTC+06:00) Dhaka', offsetHours: 6 },
    { value: 'Asia/Almaty', label: '(UTC+06:00) Almaty', offsetHours: 6 },
    { value: 'Asia/Yangon', label: '(UTC+06:30) Yangon (Rangoon)', offsetHours: 6.5 },
    { value: 'Asia/Bangkok', label: '(UTC+07:00) Bangkok, Hanoi, Jakarta', offsetHours: 7 },
    
    // UTC+8 to UTC+10
    { value: 'Asia/Shanghai', label: '(UTC+08:00) Beijing, Hong Kong, Singapore', offsetHours: 8 },
    { value: 'Asia/Taipei', label: '(UTC+08:00) Taipei', offsetHours: 8 },
    { value: 'Australia/Perth', label: '(UTC+08:00) Perth', offsetHours: 8 },
    { value: 'Asia/Tokyo', label: '(UTC+09:00) Osaka, Tokyo', offsetHours: 9 },
    { value: 'Asia/Seoul', label: '(UTC+09:00) Seoul', offsetHours: 9 },
    { value: 'Australia/Darwin', label: '(UTC+09:30) Darwin', offsetHours: 9.5 },
    { value: 'Australia/Adelaide', label: '(UTC+09:30) Adelaide', offsetHours: 9.5 },
    { value: 'Australia/Sydney', label: '(UTC+10:00) Canberra, Sydney, Melbourne', offsetHours: 10 },
    { value: 'Australia/Brisbane', label: '(UTC+10:00) Brisbane', offsetHours: 10 },
    { value: 'Pacific/Guam', label: '(UTC+10:00) Guam, Port Moresby', offsetHours: 10 },
    
    // UTC+11 to UTC+13
    { value: 'Pacific/Noumea', label: '(UTC+11:00) Solomon Islands, New Caledonia', offsetHours: 11 },
    { value: 'Pacific/Auckland', label: '(UTC+12:00) Auckland, Wellington', offsetHours: 12 },
    { value: 'Pacific/Fiji', label: '(UTC+12:00) Fiji', offsetHours: 12 },
    { value: 'Pacific/Tongatapu', label: '(UTC+13:00) Nuku\'alofa', offsetHours: 13 },
];

/**
 * Get timezone info by IANA ID
 */
export function getTimezoneInfo(timezoneId: string): TimezoneInfo | undefined {
    return TIMEZONES.find(tz => tz.value === timezoneId);
}

/**
 * Get the display label for a timezone
 */
export function getTimezoneLabel(timezoneId: string): string {
    const tz = getTimezoneInfo(timezoneId);
    return tz?.label || timezoneId;
}

/**
 * Get the current UTC offset for a timezone, accounting for DST.
 * Returns format like "UTC-05:00" or "UTC+09:00"
 */
export function getCurrentOffset(timezoneId: string): string {
    try {
        const now = new Date();
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezoneId,
            timeZoneName: 'longOffset'
        });
        
        const parts = formatter.formatToParts(now);
        const offsetPart = parts.find(p => p.type === 'timeZoneName');
        
        if (offsetPart?.value) {
            // Convert "GMT-05:00" to "UTC-05:00"
            return offsetPart.value.replace('GMT', 'UTC');
        }
        
        // Fallback to calculating offset
        const offset = getTimezoneOffsetMinutes(timezoneId, now);
        return formatOffsetString(offset);
    } catch {
        return 'UTC+00:00';
    }
}

/**
 * Get timezone abbreviation (e.g., "EST", "PST", "UTC")
 */
export function getTimezoneAbbreviation(timezoneId: string): string {
    try {
        const now = new Date();
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezoneId,
            timeZoneName: 'short'
        });
        
        const parts = formatter.formatToParts(now);
        const tzPart = parts.find(p => p.type === 'timeZoneName');
        return tzPart?.value || timezoneId;
    } catch {
        return timezoneId;
    }
}

/**
 * Detect the user's browser timezone.
 * Returns the IANA timezone ID if it's in our supported list,
 * otherwise returns 'America/New_York' as default.
 */
export function detectTimezone(): string {
    try {
        const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        // Check if the detected timezone is in our list
        if (TIMEZONES.some(tz => tz.value === detected)) {
            return detected;
        }
        
        // Try to find a matching timezone by offset
        const offset = new Date().getTimezoneOffset();
        const offsetHours = -offset / 60;
        const match = TIMEZONES.find(tz => tz.offsetHours === offsetHours);
        
        return match?.value || 'America/New_York';
    } catch {
        return 'America/New_York';
    }
}

/**
 * Convert a local datetime string to UTC.
 * 
 * @param localDatetime - Local datetime string (e.g., "2025-01-15T14:00:00")
 * @param timezoneId - IANA timezone ID (e.g., "America/New_York")
 * @returns UTC datetime string in ISO format (e.g., "2025-01-15T19:00:00Z")
 */
export function toUTC(localDatetime: string, timezoneId: string): string {
    // Parse the local datetime components
    const [datePart, timePart] = localDatetime.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes, seconds = 0] = (timePart || '00:00:00').split(':').map(n => parseInt(n) || 0);
    
    // Create a date object treating the input as if it's in the specified timezone
    // We use a workaround since JavaScript doesn't have native timezone support
    const localDate = new Date(year, month - 1, day, hours, minutes, seconds);
    
    // Get the offset for the specified timezone at this local time
    const offsetMinutes = getTimezoneOffsetMinutes(timezoneId, localDate);
    
    // Adjust to UTC by subtracting the offset
    const utcTime = new Date(localDate.getTime() - offsetMinutes * 60 * 1000);
    
    return utcTime.toISOString();
}

/**
 * Convert a UTC datetime to a Date object in the specified timezone.
 * 
 * @param utcDatetime - UTC datetime string (with or without 'Z')
 * @param timezoneId - IANA timezone ID
 * @returns Date object representing the time in the specified timezone
 */
export function fromUTC(utcDatetime: string, timezoneId: string): Date {
    // Ensure the datetime is treated as UTC
    const utcString = utcDatetime.endsWith('Z') ? utcDatetime : `${utcDatetime}Z`;
    const utcDate = new Date(utcString);
    
    // Get the offset for the target timezone
    const offsetMinutes = getTimezoneOffsetMinutes(timezoneId, utcDate);
    
    // Create a new date adjusted for the timezone
    return new Date(utcDate.getTime() + offsetMinutes * 60 * 1000);
}

/**
 * Format a UTC datetime for display in a specific timezone.
 * 
 * @param utcDatetime - UTC datetime string
 * @param timezoneId - IANA timezone ID
 * @param options - Intl.DateTimeFormat options or a preset format name
 */
export function formatForDisplay(
    utcDatetime: string,
    timezoneId: string,
    options: Intl.DateTimeFormatOptions | 'time' | 'date' | 'datetime' | 'full' = 'datetime'
): string {
    const utcString = utcDatetime.endsWith('Z') ? utcDatetime : `${utcDatetime}Z`;
    const date = new Date(utcString);
    
    let formatOptions: Intl.DateTimeFormatOptions;
    
    if (typeof options === 'string') {
        switch (options) {
            case 'time':
                formatOptions = { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: timezoneId };
                break;
            case 'date':
                formatOptions = { year: 'numeric', month: 'long', day: 'numeric', timeZone: timezoneId };
                break;
            case 'datetime':
                formatOptions = { 
                    year: 'numeric', month: 'short', day: 'numeric',
                    hour: 'numeric', minute: '2-digit', hour12: true,
                    timeZone: timezoneId 
                };
                break;
            case 'full':
                formatOptions = {
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                    hour: 'numeric', minute: '2-digit', hour12: true,
                    timeZone: timezoneId, timeZoneName: 'short'
                };
                break;
            default:
                formatOptions = { timeZone: timezoneId };
        }
    } else {
        formatOptions = { ...options, timeZone: timezoneId };
    }
    
    return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
}

/**
 * Format time only (e.g., "2:30 PM")
 */
export function formatTime(utcDatetime: string, timezoneId: string): string {
    return formatForDisplay(utcDatetime, timezoneId, 'time');
}

/**
 * Format date only (e.g., "January 15, 2025")
 */
export function formatDate(utcDatetime: string, timezoneId: string): string {
    return formatForDisplay(utcDatetime, timezoneId, 'date');
}

/**
 * Get the offset in minutes for a timezone at a specific date.
 * Positive values = ahead of UTC, negative = behind UTC.
 */
function getTimezoneOffsetMinutes(timezoneId: string, date: Date): number {
    try {
        // Get the date string in the target timezone
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezoneId,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
        
        const parts = formatter.formatToParts(date);
        const get = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0');
        
        const tzYear = get('year');
        const tzMonth = get('month') - 1;
        const tzDay = get('day');
        const tzHour = get('hour');
        const tzMinute = get('minute');
        const tzSecond = get('second');
        
        // Create a UTC date with the same components
        const utcEquivalent = Date.UTC(tzYear, tzMonth, tzDay, tzHour, tzMinute, tzSecond);
        
        // The difference gives us the offset
        return (utcEquivalent - date.getTime()) / (60 * 1000);
    } catch {
        // Fallback: use the timezone info's static offset
        const tz = getTimezoneInfo(timezoneId);
        return tz ? tz.offsetHours * 60 : 0;
    }
}

/**
 * Format offset minutes to string (e.g., -300 -> "UTC-05:00")
 */
function formatOffsetString(offsetMinutes: number): string {
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absMinutes = Math.abs(offsetMinutes);
    const hours = Math.floor(absMinutes / 60);
    const minutes = absMinutes % 60;
    
    return `UTC${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Check if a timezone ID is valid/supported
 */
export function isValidTimezone(timezoneId: string): boolean {
    return TIMEZONES.some(tz => tz.value === timezoneId);
}

/**
 * Get timezones filtered by region or search term
 */
export function searchTimezones(query: string): TimezoneInfo[] {
    const lowerQuery = query.toLowerCase();
    return TIMEZONES.filter(tz => 
        tz.label.toLowerCase().includes(lowerQuery) ||
        tz.value.toLowerCase().includes(lowerQuery)
    );
}
